import React, { LegacyRef, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getRouteApi, useRouter } from '@tanstack/react-router';
import { useAtom, useAtomValue } from 'jotai';

import { updateMediaImageName } from '@/api/media';
import PoweredByConnectLogo from '@/assets/powered-by.png';
import { Button } from '@/components/ui/button';
import TimeSpinner from '@/components/ui/time-spinner';
import { env } from '@/constants/env';
import { uploadImage as uploadImageToEstateMedia } from '@/features/estate-media/api/upload-image';
import { getEstate } from '@/features/estates-property/api/estate';
import { getFloorLabel } from '@/features/estates-property/utils/floorLabelHandler';
import { getImage } from '@/features/image-rendering/api/image';
import { useRenderImage } from '@/features/image-rendering/api/render-image';
import {
  ImageInAtom,
  selectedImageAtom,
  selectedPreviewImageAtom,
  uploadedImagesAtom,
} from '@/features/image-rendering/atoms/imageFiles';
import {
  desiredColorSettingsAtom,
  desiredStyleSettingsAtom,
  editMethodAtom,
  materialSettingsAtom,
  roomModeSettingsAtom,
  roomTypeSettingsAtom,
  settingsChangedAtom,
} from '@/features/image-rendering/atoms/settings';
import { ImageEditor } from '@/features/image-rendering/components/editor';
import { ImageRenderingHeader } from '@/features/image-rendering/components/header';
import { ImageRenderingSidebar } from '@/features/image-rendering/components/side-bar';
import { EDIT_IMAGE_METHOD } from '@/features/image-rendering/constants/toggle-group-options';
import { useElementResize } from '@/hooks/use-element-resize';
import { getRenderValue } from '@/utils/getEmptyVisualValue';
import { getNumberFormatter } from '@/utils/getNumberFormatter';
import { getPriceWithCurrency } from '@/utils/getPriceWithCurrency';
import { parseStringToBool } from '@/utils/parseStringToBool';
import { createTempId } from '@/utils/tempId';

const route = getRouteApi('/_authenticated/_sidebar-layout/image-rendering');

export type CarouselImage = {
  mediaId: string;
  sourceId?: string;
  createdAt?: string;
  updatedAt?: string;
  fileType?: string;
};

export const MIN_ZOOM = 1;

export const ImageRenderingPage = () => {
  const router = useRouter();
  if (!parseStringToBool(env.IMAGE_RENDERING)) {
    router.navigate({ to: '/' });
  }

  const { imageId: imageIdFromParams, estateId: estateIdFromParams } = route.useSearch();

  const { ref, size } = useElementResize();
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [estateId, setEstateId] = useState<string | undefined>(estateIdFromParams);
  const editMethod = useAtomValue(editMethodAtom);

  const [fileName, setFileName] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [showMobileSettings, setShowMobileSettings] = useState(false);
  const [, setSettingsChanged] = useAtom(settingsChangedAtom);

  const [, setUploadedImages] = useAtom(uploadedImagesAtom);
  const [selectedImage, setSelectedImage] = useAtom(selectedImageAtom);
  const [selectedPreviewImage] = useAtom(selectedPreviewImageAtom);
  const roomtype = useAtomValue(roomTypeSettingsAtom);
  const colors = useAtomValue(desiredColorSettingsAtom);
  const style = useAtomValue(desiredStyleSettingsAtom);
  const mode = useAtomValue(roomModeSettingsAtom);
  const materials = useAtomValue(materialSettingsAtom);

  const { mutate: renderImage } = useRenderImage({
    mutationConfig: {
      onSuccess: (data) => {
        const socket = new WebSocket(
          `${env.API_URL.replace('http', 'ws')}/ws?taskId=${data.taskId}`
        );
        toast.success(t('rendering-initiated'));

        const parseJsonError = async (data: any) => {
          try {
            const textData = await data.text();
            const jsonData = JSON.parse(textData);
            return jsonData.error || null;
          } catch {
            return null;
          }
        };

        const processImageData = async (data: any) => {
          const image = new File([data], 'image', { type: 'image/jpeg' });
          const imgUrl = URL.createObjectURL(image);

          if (estateId) {
            await uploadImageToEstateMedia({ image, estateId });
          }
          setUploadedImages((prev) => {
            const curr = prev.find((img) => img.id === selectedImage?.id);
            // should never happen, but for TS reasons
            if (!curr || !selectedImage) return prev;

            return [
              ...prev,
              {
                ...selectedImage,
                id: createTempId(),
                file: image,
                renderedImage: imgUrl,
              },
            ];
          });
          setFileName(image?.name ?? 'Undefined');
          setShowMobileSettings(false);
          setSettingsChanged(false);

          toast.success(t('rendering-complete'));
        };

        socket.onmessage = async (event) => {
          try {
            // Check for JSON error first
            const errorMessage = await parseJsonError(event.data);
            if (errorMessage) {
              toast.error(errorMessage);
              setIsLoading(false);
              socket.close();
              return;
            }

            // Otherwise Process as image data
            await processImageData(event.data);
            setIsLoading(false);
            socket.close();
          } catch (error) {
            console.error('Error processing WebSocket message:', error);
            const message =
              error instanceof Error ? error.message : 'Error processing rendering result';
            toast.error(message);
            setIsLoading(false);
            socket.close();
          }
        };

        socket.onerror = (error) => {
          console.error('WebSocket error:', error);
          toast.error(`WebSocket error: ${error}`);
          setIsLoading(false);
          socket.close();
        };
      },
      onError: (error) => {
        toast.error(error.message);
        setIsLoading(false);
      },
    },
  });

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      const id = createTempId();
      const objectUrl = URL.createObjectURL(file);
      const uploadedImage = { id, originalImage: objectUrl, fileType: file.type, file };
      setUploadedImages((prev) => [...prev, uploadedImage]);
      setSelectedImage(uploadedImage);
      setFileName(file.name);

      e.target.value = '';
    }
  };

  const handleStartImageRendering = async () => {
    if (!selectedImage) {
      return;
    }
    setIsLoading(true);
    const getImageFile = async (imgId: string | undefined) => {
      if (!imgId) return null;
      const response = await getImage(imgId);
      const blob = await response.blob();
      return new File([blob], 'image', { type: blob.type || 'image/jpeg' });
    };
    const image = selectedImage.file
      ? selectedImage.file
      : await getImageFile(selectedImage.originalImageId);
    if (!image) {
      toast.error('no valid image detected');
      return;
    }

    if (roomtype && style) {
      renderImage({
        image,
        roomtype,
        style,
        colors,
        materials,
        mode,
      });
    }
  };

  const handleDownloadImage = async () => {
    let imageUrl = null;
    const link = document.createElement('a');

    if (selectedPreviewImage?.renderedImage) {
      imageUrl = selectedPreviewImage?.renderedImage;

      const blob = await fetch(imageUrl).then((res) => res.blob());
      imageUrl = URL.createObjectURL(blob);
    } else if (selectedImage?.originalImage) {
      imageUrl = selectedImage?.originalImage;

      const blob = await fetch(imageUrl).then((res) => res.blob());
      imageUrl = URL.createObjectURL(blob);
    } else if (selectedImage?.file) {
      imageUrl = URL.createObjectURL(selectedImage?.file);
    }

    if (imageUrl) {
      link.href = imageUrl;
      link.download = 'downloaded_image.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExportImage = async () => {
    try {
      if (
        estateId &&
        (selectedPreviewImage?.renderedImage || selectedImage?.file || selectedImage?.originalImage)
      ) {
        const imageSrc =
          selectedPreviewImage?.renderedImage ||
          selectedImage?.file ||
          selectedImage?.originalImage;

        const { Document, Image, Link, Page, pdf, StyleSheet, Text, View } = await import(
          '@react-pdf/renderer'
        );

        const styles = StyleSheet.create({
          infoBox: {
            paddingLeft: '5%',
            paddingRight: '5%',
            marginBottom: '15%',
            marginTop: '5%',
          },
          rowView: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            paddingTop: '1%',
            alignItems: 'center',
          },
          footerBox: {
            paddingLeft: '5%',
            paddingRight: '5%',
            marginBottom: '3%',
            marginTop: '5%',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
          },
          title: {
            fontSize: 17,
            fontWeight: 700,
          },
          text: {
            fontSize: 13,
            fontWeight: 400,
          },
          link: {
            fontSize: 13,
            fontWeight: 400,
            color: 'initial',
          },
          nameBox: {
            maxWidth: '75%',
          },
          companyLink: {
            fontSize: 8,
            fontWeight: 500,
            color: 'initial',
            textDecoration: 'none',
          },
          tenantLogo: {
            height: '40px',
          },
          connectLogo: {
            width: '58px',
          },
          mainContainer: {
            width: '100%',
            alignItems: 'center',
            paddingLeft: '5%',
            paddingRight: '5%',
          },
          mainFile: {
            maxHeight: '480px',
          },
        });

        const estate = await getEstate(estateId);

        const blob = await pdf(
          <Document>
            <Page>
              <View style={styles.infoBox}>
                <View style={styles.rowView}>
                  <Text style={{ ...styles.title, ...styles.nameBox }}>{estate.name}</Text>
                  <Text style={styles.title}>
                    {getPriceWithCurrency(
                      estate.prices?.buyingPrice,
                      estate.prices?.currency,
                      'de-DE'
                    )}
                  </Text>
                </View>
                <View style={styles.rowView}>
                  <Text></Text>
                  <Text style={styles.text}>
                    {t('area-value-without-approx', {
                      value: getRenderValue(
                        getNumberFormatter(estate.estateAreasRooms?.usableArea, 'de-DE')
                      ),
                    })}
                  </Text>
                </View>
                <View style={styles.rowView}>
                  <Text style={styles.text}>{estate.projects?.name}</Text>
                  <Text style={styles.text}>
                    {getNumberFormatter(estate.estateAreasRooms?.totalNumberOfRooms, 'de-DE', {
                      maximumFractionDigits: 1,
                      minimumFractionDigits: 1,
                    })}{' '}
                    {t('totalNumberOfRooms')}
                  </Text>
                </View>
                <View style={styles.rowView}>
                  <Text style={styles.text}>{estate.estateDetails?.externalId}</Text>
                  <Text style={styles.text}>
                    {getFloorLabel(estate.estateAreasRooms?.floor || 0)}
                  </Text>
                </View>
                <View style={styles.rowView}>
                  <Text></Text>
                  {estate.estateDetails?.externalLink ? (
                    <Link style={styles.link} href={estate.estateDetails?.externalLink}>
                      {t('details')}
                    </Link>
                  ) : null}
                </View>
              </View>

              <View style={styles.mainContainer}>
                <Image style={styles.mainFile} src={imageSrc} />
              </View>

              <View style={styles.footerBox} fixed>
                <View style={styles.rowView}>
                  <Image
                    src={`${env.API_URL}/images/${estate.tenant?.logoId}`}
                    style={styles.tenantLogo}
                  />
                  <View>
                    <Image src={PoweredByConnectLogo} style={styles.connectLogo} />
                    <Link style={styles.companyLink} href="https://www.realitiq.com">
                      www.realitiq.com
                    </Link>
                  </View>
                </View>
              </View>
            </Page>
          </Document>
        ).toBlob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'exported-image.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url); // Clean up
        document.body.removeChild(a); // Clean up
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  const handleChangeFileName = async (newVal: string) => {
    if (estateId && selectedImage?.originalImageId && newVal) {
      updateMediaImageName(estateId, selectedImage?.originalImageId, newVal).then(() => {
        setFileName(newVal);
      });
    }
  };

  const handleClickFloatingButton = () => {
    if (containerRef.current) {
      const containerRefCurrent: Element = containerRef.current;
      containerRefCurrent.scrollTo({
        top: containerRefCurrent.scrollHeight,
        behavior: 'smooth', // Enables smooth scrolling
      });
    }
  };

  const checkScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      setShowFloatingButton(scrollTop + clientHeight + 80 < scrollHeight);
    }
  };

  useEffect(() => {
    // if the route has been accessed with the imageId in the params, set is as the selected image and add it to the uploadedImages
    if (imageIdFromParams) {
      const imageFromParams = {
        id: createTempId(),
        fileType: 'image/jpeg',
        file: null,
        originalImage: `${env.API_URL}/images/${imageIdFromParams}`,
        originalImageId: imageIdFromParams,
      } satisfies ImageInAtom;
      setSelectedImage(imageFromParams);
      setUploadedImages((prev) => [...prev, imageFromParams]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageIdFromParams]);

  useEffect(() => {
    if (containerRef?.current) {
      const { scrollHeight, clientHeight } = containerRef.current;

      setShowFloatingButton(scrollHeight > clientHeight + 1);
    }
  }, [size, containerRef]);

  useEffect(() => {
    const abortController = new AbortController();

    if (containerRef.current) {
      const containerRefCurrent: Element = containerRef.current;
      containerRefCurrent.addEventListener('scroll', checkScroll, {
        signal: abortController.signal,
      });
    }

    return () => {
      abortController.abort();
    };
  }, []);

  const sidebarRef = useCallback(
    (node: Element) => {
      if (!node) return;
      const resizeObserver = new ResizeObserver(() => {
        const { clientHeight } = node;

        if (
          (editMethod === EDIT_IMAGE_METHOD.STYLE && clientHeight < 500) ||
          (editMethod === EDIT_IMAGE_METHOD.MAGIC && clientHeight < 600)
        ) {
          setShowFloatingButton(false);
        }
      });
      resizeObserver.observe(node);
    },
    [editMethod]
  );

  return (
    <>
      <TimeSpinner isLoading={isLoading} />
      <div
        ref={containerRef}
        className="relative mr-0 h-full overflow-y-auto pr-0 sm:-mr-[46px] sm:pr-[46px]"
      >
        <div ref={ref} className="relative flex flex-1 flex-col">
          <ImageRenderingHeader
            estateId={estateId}
            imageName={fileName}
            onChangeImageName={handleChangeFileName}
            onBack={() => setShowMobileSettings(false)}
          />
          <div className="relative flex flex-1 flex-col items-start gap-2 p-1 sm:flex-row sm:gap-7 sm:p-4 lg:py-6 lg:pl-8 lg:pr-8">
            <ImageRenderingSidebar
              disableSelectObject={!!estateIdFromParams}
              estateId={estateId}
              onChangeEstateId={setEstateId}
              onStartRenderingImage={handleStartImageRendering}
              onUploadImage={handleUploadImage}
              showMobileSettings={showMobileSettings}
              sidebarRef={sidebarRef as unknown as LegacyRef<HTMLDivElement>}
            />
            <ImageEditor
              onDownload={handleDownloadImage}
              onExport={handleExportImage}
              setShowMobileSettings={setShowMobileSettings}
              showMobileSettings={showMobileSettings}
            />
          </div>
        </div>
        {showFloatingButton ? (
          <Button
            className="sticky bottom-[60px] left-[50%] mt-2 h-[34px] w-[120px] flex-1 -translate-x-[50%] py-1 sm:left-[32px] sm:translate-x-0 lg:left-[121px]"
            onClick={handleClickFloatingButton}
          >
            {t('create')}
          </Button>
        ) : null}
      </div>
    </>
  );
};
